import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import Map from "./Map";
import ProjectDetails from "./ProjectDetails";
import "./App.css";
import Logo from "./assets/Hasiru-Farms-Logo.png";

function MainContent({ showForm, setShowForm, showThankYou, setShowThankYou, formData, setFormData, formError, setFormError, formSubmitted, setFormSubmitted, plots }) {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check localStorage and route for form display
  useEffect(() => {
    const hasSubmitted = localStorage.getItem("formSubmitted");
    const isHomePage = location.pathname === "/" || location.pathname === "/map";

    if (!hasSubmitted && isHomePage) {
      setShowForm(true);
    } else {
      setFormSubmitted(true);
    }
  }, [location.pathname, setShowForm, setFormSubmitted]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validate and submit form
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const { name, mobileNumber, email } = formData;

    // Basic validation
    if (!name || name.length < 2) {
      setFormError("Please enter a valid name (at least 2 characters).");
      return;
    }
    if (!/^\d{10}$/.test(mobileNumber)) {
      setFormError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    setFormError("");
    setIsSubmitting(true);

    try {
      // Save to Firestore with projectName set to "Prakruthi"
      await addDoc(collection(db, "leads"), {
        ...formData,
        projectName: "Prakruthi",
        timestamp: serverTimestamp(),
      });

      // Mark form as submitted in localStorage
      localStorage.setItem("formSubmitted", "true");
      setShowForm(false);
      setShowThankYou(true);
      setFormError("");

      // Transition to map after 2 seconds
      setTimeout(() => {
        setShowThankYou(false);
        setFormSubmitted(true);
      }, 2000);
    } catch (error) {
      console.error("Error saving lead to Firestore:", error);
      setFormError("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showThankYou) {
    return (
      <div className="hfe-thank-you-container">
        <div className="hfe-thank-you-content">
          <span className="hfe-success-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          </span>
          <h2>You're all set!</h2>
          <p>Thanks for your details — opening the plot map for you…</p>
        </div>
      </div>
    );
  }

  if (showForm && !formSubmitted) {
    return (
      <div className="hfe-form-container">
        <div className="hfe-form-card">
          {/* Branded side panel */}
          <aside className="hfe-form-aside">
            <div className="hfe-aside-inner">
              <img src={Logo} alt="Prakruthi" className="hfe-form-logo" />
              <h1 className="hfe-aside-title">Discover Prakruthi</h1>
              <p className="hfe-aside-sub">
                Thoughtfully planned farmland, ready to explore. Share a few
                details to unlock the interactive plot map.
              </p>
              <ul className="hfe-aside-points">
                <li>
                  <span className="hfe-point-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                  Verified layout &amp; live plot availability
                </li>
                <li>
                  <span className="hfe-point-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                  Instant access to the interactive map
                </li>
                <li>
                  <span className="hfe-point-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                  Personal guidance from our team
                </li>
              </ul>
            </div>
          </aside>

          {/* Form panel */}
          <div className="hfe-form-panel">
            <div className="hfe-form-header">
              <h2 className="hfe-form-heading">Get instant access</h2>
              <p className="hfe-form-subheading">
                It takes less than a minute — no obligations.
              </p>
            </div>

            <form onSubmit={handleFormSubmit} className="hfe-entry-form" noValidate>
              <div className="hfe-form-group">
                <label htmlFor="name">Full Name</label>
                <div className="hfe-input-wrap">
                  <span className="hfe-input-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  </span>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Anitha Rao"
                    autoComplete="name"
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              <div className="hfe-form-group">
                <label htmlFor="mobileNumber">Mobile Number</label>
                <div className="hfe-input-wrap">
                  <span className="hfe-input-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  </span>
                  <input
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                    maxLength="10"
                    autoComplete="tel"
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              <div className="hfe-form-group">
                <label htmlFor="email">Email Address</label>
                <div className="hfe-input-wrap">
                  <span className="hfe-input-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                  </span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                    autoComplete="email"
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              {formError && (
                <p className="hfe-form-error" role="alert">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                  {formError}
                </p>
              )}

              <button type="submit" className="hfe-submit-button" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="hfe-btn-spinner" aria-hidden="true" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Explore the Map
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </>
                )}
              </button>
            </form>

            <p className="hfe-form-privacy">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              Your details are safe with us. Hasiru Farms Enterprises Private Limited.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <Map plots={plots} />;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [plots, setPlots] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    email: "",
  });
  const [formError, setFormError] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Fetch plots
  useEffect(() => {
    const fetchFirestoreData = async () => {
      try {
        const plotsCollection = collection(db, "prakruthiplots");
        const plotsSnapshot = await getDocs(plotsCollection);
        const plotsData = plotsSnapshot.docs.map((doc) => ({
          projectId: doc.id,
          ...doc.data(),
        }));
        setPlots(plotsData);
      } catch (error) {
        console.error("Error fetching Firestore data:", error);
        setPlots([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFirestoreData();

    const timeout = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timeout);
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-container">
          <img src={Logo} alt="Logo" className="loading-logo" />
          <div className="loader"></div>
          <p>Loading Map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <MainContent
                showForm={showForm}
                setShowForm={setShowForm}
                showThankYou={showThankYou}
                setShowThankYou={setShowThankYou}
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                setFormError={setFormError}
                formSubmitted={formSubmitted}
                setFormSubmitted={setFormSubmitted}
                plots={plots}
              />
            }
          />
          <Route
            path="/map"
            element={
              <MainContent
                showForm={showForm}
                setShowForm={setShowForm}
                showThankYou={showThankYou}
                setShowThankYou={setShowThankYou}
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                setFormError={setFormError}
                formSubmitted={formSubmitted}
                setFormSubmitted={setFormSubmitted}
                plots={plots}
              />
            }
          />
          <Route path="/project/:projectId" element={<ProjectDetails />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;